// 游戏状态仓库（EP-002-S6 / S7）—— Zustand 单例。
//
// 设计要点：
// - `save` 是唯一真相；`phase` 由 save 推导（不冗余存）。
// - setSave 类操作集中于此，UI 只通过 selector + action 改状态（不直接 setSave）。
// - `lastSettledTripId` 的写入**只**发生在 src/domain/settleTrip.ts（CI 守门 check-settle-ownership）；
//   本仓库的 settle 动作委托给它，绝不自行赋值该字段。
// - 计时严格走真实时间差（computeElapsed），store 不累加任何 elapsed（CI 守门 check-no-tick-accumulator）。
import { create } from 'zustand';
import type { SaveDataV1, DestinationId, TripV1, PostcardEntry } from '../save/types';
import { CURRENT_SCHEMA_VERSION } from '../save/constants';
import { loadSave } from '../save';
import { constructStorageAdapter } from '../save/storage';
import { now } from '../core/time';
import { computeElapsed } from '../core/timer';
import { randomRng } from '../core/rng';
import { newTripId, newId } from '../core/id';
import { dropConfig } from '../domain/drop/dropConfig';
import { planReturn } from '../domain/drop/rollOutcome';
import { pureSettle } from '../domain/settleTrip';
import { rollPostcard } from '../domain/travel/postcardRoll';
import { POSTCARD_NOTE_BY_ID } from '../runtime/postcardNotes';
import { addToInventory as addToInventoryDomain } from '../domain/inventory';
import {
  revealSnapshot,
  noteDedupeKey,
  EMPTY_HANDS_DEDUPE_KEY,
  type RevealSnapshot,
} from '../runtime/sequencer';
import { setAdapter, persistNow, subscribeToStore } from './persist';

// 🆕 STEP 5-B：明信片在 tick 内 t=0.5 首次跨越时一次性 roll（仅一次）。
//   这里抽到的概率 0.55、缺席 0.45 见 rollPostcard 的 POSTCARD_SEND_PROBABILITY。
//   阈值 0.5 与模糊档位 2 ([0.15, 0.40)) 紧邻，玩家感知"刚走出家门"就有明信片寄出。
export const POSTCARD_TRIGGER_PROGRESS = 0.5;

export type Phase = 'no-save' | 'home' | 'away' | 'awaitingReveal';
export type StorageState =
  | 'uninitialized'
  | 'ok'
  | 'empty'
  | 'corrupt'
  | 'futureVersion'
  | 'storageUnavailable';

// 视图层 UI 态（非存档、不落盘，刷新即复位）：home=主场景 / reveal=揭晓演出 / space=空间背包。
export type View = 'home' | 'reveal' | 'space';

// 🆕 v2：空间背包内 tab（不落盘，刷新即复位）。StatusCard 角标点击入空间时设 'postcards'。
export type SpaceTab = 'gifts' | 'postcards';

export interface GameStore {
  save: SaveDataV1 | null;
  storageState: StorageState;
  clock: number; // 仅供模糊文案触发重渲染，不承载任何时间/进度语义
  view: View; // UI 视图态（home / reveal / space）
  revealSnapshot: RevealSnapshot | null; // 揭晓演出只读快照（先入库后构造）
  dogLeaving: boolean; // 离场演出中（纯 UI 态，不落盘；动画结束即复位）
  spaceTab: SpaceTab; // 🆕 v2：空间 tab（home / space 切换时复位默认 'gifts'，从角标入 'postcards'）

  init: () => void;
  depart: (travelDurationMs: number, destinationId: DestinationId, isLongTrip?: boolean) => void;
  endDogLeave: () => void; // 离场动画结束回调（onAnimationEnd / 兜底计时器都会调）
  tick: (t: number) => void;
  markRevealReady: () => void;
  settle: () => void;
  addToInventory: (giftId: string, count?: number) => void;
  beginReveal: () => void; // 先快照 → 结算落盘 → 进 reveal 演出（EP-007-S4）
  endReveal: () => void; // 演出结束 → 进 space
  openSpace: (initialTab?: SpaceTab) => void; // 进入空间（home/away/awaitingReveal 皆可，老板 2026-09-15 拍板；v2 新增可选入参）
  closeSpace: () => void; // 从空间返回 home
  // 🆕 STEP 5-B：详情页打开 → 标记某条明信片已读（红点 -1）。
  markPostcardRead: (entryId: string) => void;
  // 🆕 v2.1：礼物详情打开 → 标记某 stack 已读（礼物 tab 角标 -1；TopBar 空间按钮合计未读 -1）。
  markGiftRead: (giftId: string) => void;
}

// 由 save 推导 phase（唯一真相在 save，不冗余存）。
export function selectPhase(s: Pick<GameStore, 'save'>): Phase {
  if (s.save === null) return 'no-save';
  // dogState 取值恰为 home / away / awaitingReveal
  return s.save.dogState;
}

// 回写留言去重键（GDD 02 §②-2.7「同一目的地连续两次不重复」）。
// 两条结算路径（beginReveal / settle）**共用本函数**，保证「选」与「记」同源、规则不可能漂移：
// 键由 runtime/sequencer 的 noteDedupeKey 推导（v2：移除明信片 'postcard' 键 → 仅 'longtrip' / dest）。
// 🆕 P6-POLISH-ENG-002 §B.5：空手归来时留言走空手池（EMPTY_HANDS_NOTES），其去重键是
//   `EMPTY_HANDS_DEDUPE_KEY`（与 'longtrip' / dest 同级），必须按"是否空手"分写，
//   否则空手留言的"连续不重复"判定永远读不到上次那一条（每次都当全新）。
// trip 为空（未真正结算）时原样返回，绝不写键。
function withNoteDedupe(next: SaveDataV1, noteId: string, trip: TripV1 | null): SaveDataV1 {
  if (!trip) return next;
  // 空手判定：有 dropResult 且 giftIds 为空 ⇔ planReturn 判定的空手（roll.ts 红线保证正常掉落恒 ≥1）。
  const emptyHanded = trip.dropResult !== null && trip.dropResult.giftIds.length === 0;
  const key = emptyHanded
    ? EMPTY_HANDS_DEDUPE_KEY
    : noteDedupeKey(trip.isLongTrip, trip.destinationId);
  return {
    ...next,
    lastNoteIdByDestination: { ...next.lastNoteIdByDestination, [key]: noteId },
  };
}

function createFreshSave(): SaveDataV1 {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    dogState: 'home',
    trip: null,
    lastSettledTripId: null,
    stats: { tripCount: 0, isFirstTripDone: false },
    lastNoteIdByDestination: {},
    settings: { timeScale: 1, duskTintEnabled: false },
    inventory: [],
    savedAt: now(),
    // 🆕 v2：mailbox + lastPostcardNoteIdByDestination 必填字段。
    mailbox: [],
    lastPostcardNoteIdByDestination: {},
  };
}

let subscribed = false;

export const useGameStore = create<GameStore>()((set, get) => ({
  save: null,
  storageState: 'uninitialized',
  clock: 0,
  view: 'home',
  revealSnapshot: null,
  dogLeaving: false,
  spaceTab: 'gifts', // 🆕 v2：空间 tab 默认 'gifts'

  init: () => {
    const adapter = constructStorageAdapter();
    setAdapter(adapter);
    const result = loadSave(adapter);

    let storageState: StorageState = 'ok';
    let save: SaveDataV1 | null = null;

    switch (result.kind) {
      case 'ok':
        save = result.save;
        break;
      case 'empty':
        save = createFreshSave();
        persistNow(save); // 首次启动写一份新档
        storageState = 'empty';
        break;
      case 'corrupt':
        // T9：备份已在 loadSave 内完成，这里重置为新档（保留提示）
        save = createFreshSave();
        persistNow(save);
        storageState = 'corrupt';
        break;
      case 'futureVersion':
        // T8：只读、绝不写回、不重置（提示用户「版本太新」）
        save = null;
        storageState = 'futureVersion';
        break;
      case 'storageUnavailable':
        // T10：降级内存态，游戏可玩，提示「无法保存」
        save = createFreshSave();
        storageState = 'storageUnavailable';
        break;
    }

    set({ save, storageState, clock: get().clock + 1 });

    if (!subscribed) {
      subscribeToStore();
      subscribed = true;
    }
  },

  depart: (travelDurationMs, destinationId, isLongTrip = false) => {
    const save = get().save;
    // T6：非 home 态不可出发
    if (!save || save.dogState !== 'home') return;

    const trip: TripV1 = {
      id: newTripId(),
      departureTimestamp: now(),
      travelDurationMs,
      destinationId,
      isLongTrip,
      status: 'inProgress',
      dropResult: null,
      noteId: '', // 留言在 EP-007 接入；§13-A 已预留字段
    };

    set({ save: { ...save, dogState: 'away', trip }, dogLeaving: true, clock: get().clock + 1 });
    // 兜底计时器：动画 end 事件被节流/吞掉时也能复位（1600ms = 动画 1.5s + 缓冲）
    window.setTimeout(() => {
      if (get().dogLeaving) set({ dogLeaving: false });
    }, 1600);
  },

  endDogLeave: () => {
    if (get().dogLeaving) set({ dogLeaving: false, clock: get().clock + 1 });
  },

  tick: (t: number) => {
    const save = get().save;
    if (!save || save.dogState !== 'away' || !save.trip) {
      // 非在途：仍 bump clock 让 UI 在 home/awaitingReveal 下保持一致（无副作用）
      set((s) => ({ clock: s.clock + 1 }));
      return;
    }

    const { progress, ready } = computeElapsed(
      save.trip.departureTimestamp,
      t,
      save.trip.travelDurationMs,
    );

    let nextSave = save;

    // 🆕 STEP 5-B：明信片在 t=0.5（progress >= POSTCARD_TRIGGER_PROGRESS）首次跨越时
    //   一次性 roll。判定用 mailbox 中是否已有 tripId 关联的 entry —— 有了就不再抽
    //   （无论后续 tick 多少次 progress 始终 >= 0.5，只触发一次）。
    //   离线 8h 后第一次 tick：progress=8 (clamped to 1)，有 mailbox[tripId] ? 否 → roll 一次。
    const hasRolledForTrip = save.mailbox.some((p) => p.tripId === save.trip!.id);
    if (!hasRolledForTrip && progress >= POSTCARD_TRIGGER_PROGRESS) {
      const postcardRoll = rollPostcard(
        randomRng(),
        {
          destinationId: save.trip.destinationId,
          // 🆕 P6-POLISH-ENG-002 §E.4：去重集合**由 mailbox 派生**，不再读 lastPostcardNoteIdByDestination。
          //   这样"我记得你收到过哪些"永远等于"你实际收到过哪些"，两者不可能漂移。
          receivedNoteIds: new Set(save.mailbox.map((e) => e.noteId)),
          // 用当前 tripCount（结算时才是 tripCount+1；这里路上寄，与 tripCount 平齐）
          tripCount: save.stats.tripCount,
        },
        t,
      );
      if (postcardRoll.sent && postcardRoll.note) {
        const def = POSTCARD_NOTE_BY_ID[postcardRoll.note.id];
        if (def) {
          const entry: PostcardEntry = {
            id: newId(),
            tripId: save.trip.id,
            destinationId: save.trip.destinationId,
            noteId: def.id,
            stampedAt: postcardRoll.rolledAt,
            receivedAt: t,
            imageAsset: null,
            isRead: false, // 🆕 STEP 5-B：路上寄出时为未读，玩家打开详情后才转已读
          };
          nextSave = {
            ...nextSave,
            mailbox: [...nextSave.mailbox, entry],
            // ⚠️ P6-POLISH-ENG-002 §E.4：`lastPostcardNoteIdByDestination` **停止写入**
            //   （字段保留不删，避免 migrate / sanitize 的改动面；去重已改由 mailbox 派生）。
            //   继续写会让它再次变成第二真相源，正是本次要消除的漂移来源。
          };
        }
      } else {
        // 缺席：不入 mailbox，只推进 dedupe？不推进——缺席不写键，避免污染去重。
        // 这一行是"明信片缺席 = 啥都不发生"的语义注释。
      }
    }

    if (ready) {
      // ②-2.3a：先生成掉落并落盘，再改 awaitingReveal（顺序不可颠倒，避免「已归来却无礼物」）
      //
      // 🆕 P6-POLISH-ENG-002 §B.3：把 roll() 换成 planReturn() 包装器。
      //   roll.ts 是 GDD 01 红线的载体（giftIds 恒 ≥ 1），**一行未改**；
      //   空手与否是**归来策略**，由 rollOutcome.planReturn 在上层决定。
      //   ⚠️ rng 消耗比原先 **+1 次**（空手判定），仅在 ready 时消耗 —— 脚本化 rng 测试需留意。
      const outcome = planReturn({
        config: dropConfig,
        destination: save.trip.destinationId,
        tripCount: save.stats.tripCount,
        isFirstTrip: !save.stats.isFirstTripDone, // 首旅永不空手
        lastReturnWasEmpty: save.lastReturnWasEmpty === true, // 连续两次空手禁止
        rng: randomRng(),
        now: t,
      });
      const trip: TripV1 = { ...save.trip, status: 'awaitingReveal', dropResult: outcome.dropResult };
      nextSave = { ...nextSave, dogState: 'awaitingReveal', trip };
    }

    set({ save: nextSave, clock: get().clock + 1 });
  },

  // 注：本路径同样需写 `lastNoteIdByDestination` 去重键（与 beginReveal 同源，勿删）——
  // 它最终也走 settle()，写入在 settle 内统一完成。
  markRevealReady: () => {
    const save = get().save;
    if (!save || save.dogState !== 'awaitingReveal' || !save.trip) return;
    const dropResult = save.trip.dropResult;
    if (!dropResult) return; // 异常：awaitingReveal 必有 dropResult（C4 顺序约束保证）
    get().settle();
  },

  // 注：本路径同样需写 `lastNoteIdByDestination` 去重键（与 beginReveal 同源，勿删）——
  // 见下方 withNoteDedupe 调用；当前 UI 走 beginReveal，但本 action 仍可被调用，行为必须一致。
  // 🆕 STEP 5-B：明信片不再由 settle 触发 —— 改为 tick 在 t=0.5 触发（详见 tick 注释）。
  settle: () => {
    const save = get().save;
    if (!save) return;
    // 委托 domain/settleTrip（lastSettledTripId 唯一写入者）；先入库后关闸门在内部完成
    const next = pureSettle(save, { now: now() });
    if (next === save) {
      // 守卫命中（trip 为空 / 非 awaitingReveal / 已结算）：未真正结算，不写去重键
      set({ save: next, clock: get().clock + 1 });
      return;
    }
    // 真正结算：与 beginReveal 同源写入去重键（留言选取同样走 revealSnapshot，规则一致）
    const withNote = withNoteDedupe(next, revealSnapshot(save).noteId, save.trip);
    set({ save: withNote, clock: get().clock + 1 });
  },

  beginReveal: () => {
    const save = get().save;
    if (!save || save.dogState !== 'awaitingReveal' || !save.trip) return;
    // 1) 先构造演出快照（trip 仍存）；2) 再结算落盘（关闸门）；3) 进 reveal 演出。
    // 任何中断（切标签/刷新）时礼物已在 inventory，重新进入直接见结果（ADR-004 §2.5）。
    const snapshot = revealSnapshot(save);
    // 🆕 STEP 5-B：明信片在 tick 内 t=0.5 已寄出，settle 不再 roll。
    const next = pureSettle(save, { now: now() });

    // 回写留言去重键（GDD 02 §②-2.7「同一目的地连续两次不重复」）。
    // 此前该字段全仓只有读（sequencer）没有写，去重形同虚设——本次补上写入点。
    // 与 settle() 共用 withNoteDedupe（见下），两条结算路径同源。
    // 注意：与 check-settle-ownership 守门的 lastSettledTripId 无关，后者仍只在 domain/settleTrip.ts 写。
    // save.trip 用原始引用（pureSettle 已把 next.trip 置 null）。
    const withNote = withNoteDedupe(next, snapshot.noteId, save.trip);

    persistNow(withNote); // 写入时机：揭晓落盘（主架构 §6.4 步骤 D）
    set({ save: withNote, revealSnapshot: snapshot, view: 'reveal', clock: get().clock + 1 });
  },

  endReveal: () => {
    // 演出结束 → 进包裹（礼物已落盘，纯 UI 切换）。
    // spaceTab 显式归位 'gifts'：揭晓后玩家要看的就是刚带回来的礼物，
    // 不能被上一次浏览残留的 tab（如 'postcards'）顶掉（P6-POLISH-ENG-001）。
    set({ view: 'space', revealSnapshot: null, spaceTab: 'gifts', clock: get().clock + 1 });
  },

  openSpace: (initialTab?: SpaceTab) => {
    const save = get().save;
    if (!save) return;
    // 老板 2026-09-15 反馈离家也想看包裹：home/away/awaitingReveal 皆可进入。
    // 🆕 v2：可选入参 initialTab；不传时默认 'gifts'（从 StatusCard 角标入传 'postcards'）。
    //
    // ⚠️ 白名单兜底（P6-POLISH-ENG-001）：**不能用 `initialTab ?? 'gifts'`**。
    //   曾有调用点把本 action 直接当 onClick 传，React 的 MouseEvent 落进 initialTab ——
    //   它不是 null/undefined，`??` 判不出错，spaceTab 被写成 Event 对象，
    //   结果礼物/明信片两个 tab 都不命中 —— 包裹打开了却是一片空白，得手点「礼物」才有内容。
    //   这里显式只认联合字面量：非 'postcards' 一律落到 'gifts'，宁可偏默认也绝不进脏值。
    const tab: SpaceTab = initialTab === 'postcards' ? 'postcards' : 'gifts';
    set({ view: 'space', spaceTab: tab, clock: get().clock + 1 });
  },

  closeSpace: () => {
    set({ view: 'home', spaceTab: 'gifts', clock: get().clock + 1 });
  },

  addToInventory: (giftId, count = 1) => {
    const save = get().save;
    if (!save) return;
    const inventory = addToInventoryDomain(save.inventory, [{ giftId, count }], now());
    set({ save: { ...save, inventory }, clock: get().clock + 1 });
  },

  // 🆕 STEP 5-B 引入 / STEP 5-D 改调用点：
  //   - 5-B：由 PostcardDetail 的 mount useEffect 调（依赖 effect 调度，真浏览器不可靠）
  //   - 5-D：**改由 SpaceView 列表项点击处同步调**（先落盘 → 再 setOpenPostcardId 开详情），
  //     时序确定、不依赖 React effect 调度 / StrictMode 双挂载。
  //   行为：entry.isRead = true → 主页红点（mailbox.filter(!isRead).length）-1。
  //   幂等：重复点同一 entry 不重复触发（已是 true 就跳过，L338）。
  markPostcardRead: (entryId: string) => {
    const save = get().save;
    if (!save) return;
    const idx = save.mailbox.findIndex((p) => p.id === entryId);
    if (idx < 0) return; // 不存在
    if (save.mailbox[idx]!.isRead) return; // 已读 → 跳过（不重写、不 bump clock 无关状态）
    const nextMailbox = save.mailbox.map((p, i) =>
      i === idx ? { ...p, isRead: true } : p,
    );
    set({ save: { ...save, mailbox: nextMailbox }, clock: get().clock + 1 });
  },

  // 🆕 v2.1：礼物详情打开 → 标记该 giftId 的 stack 为已读。
  // 幂等：已是 true 跳过（不重写、不 bump clock）。
  markGiftRead: (giftId: string) => {
    const save = get().save;
    if (!save) return;
    const idx = save.inventory.findIndex((s) => s.giftId === giftId);
    if (idx < 0) return; // 不存在
    if (save.inventory[idx]!.isRead) return; // 已读 → 跳过
    const nextInventory = save.inventory.map((s, i) =>
      i === idx ? { ...s, isRead: true } : s,
    );
    set({ save: { ...save, inventory: nextInventory }, clock: get().clock + 1 });
  },
}));
